/**
 * @module linkage/qbFields/qbLinkage/handlers/columnSort/qbLinkageHandlers
 * @description 字段列排序（column_sort）与 QB 的联动入口
 *
 * 业务背景：
 * - 表格展示列顺序由「字段列排序」配置决定，用户可在 QB（维/指区）与右侧设置面板两处影响它。
 * - QB 改字段结构（增删、改名、对比时间、对照组等）或调整维/指顺序时，列排序需随之对齐，
 *   否则图表列与 QB 不一致。
 * - 用户在设置面板拖拽列序后尚未 Confirm 前，该顺序属于「待保存草稿」；此时不应被
 *   后台误触发的联动覆盖，但用户在 QB 的真实操作仍应生效。
 *
 * 职责拆分：
 * - columnSortSanitizeHandler：QB 字段集合/语义变化（非纯拖拽）→ 裁剪、补缺、刷新展示名
 * - columnSortReorderHandler：QB 维区/指区纯重排 → 列排序跟随 QB 字段顺序
 */

import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { detectCompareTime } from '../../detectChanges/detectCompareTime';
import type { IQBLinkageHandler } from '../../types';
import { patchBoth } from '../shared';
import { buildColumnSortFromQB } from './fromQB/buildFromQB';
import type { IColumnPercentageItem } from './untils/columnPercentage';
import type { IDimensionPercentageConfig } from '@dbl/pages/Multidimensional/Analysis/components/ChartSettingsV2/types/settings/dimensionPercentage';
import {
  hasPersistedColumnSort,
  isColumnSortSanitizeOutputEqual,
  isStalePanelColumnSortRevert,
} from './untils/keys';
import { applyCompareTimeToColumnSort } from './syncFromQB/compareTimeUserFilter';
import {
  realignDimensionsToQBOrder,
  realignMetricsToQBOrder,
} from './syncFromQB/realignColumnSortOrder';
import {
  applyDisplayRename,
  migrateColumnSortIdentity,
  reconcileColumnSortWithQB,
} from './syncFromQB/syncOps';

/** 取用户当前认定的列序：面板未 Confirm 的 draft 优先，否则用已保存的 base */
function currentSort(ctx: {
  base: Record<string, unknown>;
  draft: Record<string, unknown>;
}): IColumnSortItem[] {
  const draft = ctx.draft.column_sort;
  if (Array.isArray(draft)) {
    return draft as IColumnSortItem[];
  }
  return (ctx.base.column_sort as IColumnSortItem[]) || [];
}

/** 列序 reorder / updateColumnSortInList 所需的 QB 上下文 */
function buildOpsCtx(ctx: Parameters<IQBLinkageHandler['apply']>[1]) {
  return {
    columnDimensions: ctx.qb.columnDimensions,
    columnMetrics: ctx.qb.columnMetrics,
    columnFilters: ctx.qb.columnFilters,
    columnUserFilters: ctx.qb.columnUserFilters,
  };
}

/**
 * 从当前 QB 筛选区解析对比时间状态。
 * changeSet.compareTime 仅在 compareTime 类别变更时有值；维区重排/删维时需单独解析。
 */
function resolveCompareTimeFromCtx(
  ctx: Parameters<IQBLinkageHandler['apply']>[1],
) {
  return detectCompareTime(ctx.qb.columnFilters);
}

/** reconcile / reorder 后统一补齐对比时间 is_compare_date */
function applyCompareTimePatchIfNeeded(
  list: IColumnSortItem[],
  ctx: Parameters<IQBLinkageHandler['apply']>[1],
): IColumnSortItem[] {
  const compareTime = resolveCompareTimeFromCtx(ctx);
  if (!compareTime.enabled && !list.some(item => item.is_compare_date)) {
    return list;
  }
  return applyCompareTimeToColumnSort(
    list,
    compareTime,
    ctx.qb.columnDimensions || [],
    ctx.qb.columnFilters,
  );
}

/**
 * 决定是否将联动结果写回 base/draft。
 *
 * 业务规则：
 * 1. 面板有未 Confirm 的列序改动，且联动结果与已保存顺序相同 → 视为误触，不写回
 *    （典型：只在设置面板拖列，QB 未变，后台 pipeline 不应把列表弹回）
 * 2. 联动结果与输入无实质差异 → 不写回，避免无意义 Confirm 脏数据
 * 3. 其余情况（含 QB 真实重排、删字段等）→ 同时更新 base 与 draft，保证预览与 QB 一致
 */
function finalizeColumnSortPatch(
  ctx: Parameters<IQBLinkageHandler['apply']>[1],
  current: IColumnSortItem[],
  next: IColumnSortItem[],
): ReturnType<IQBLinkageHandler['apply']> {
  if (isStalePanelColumnSortRevert(ctx, next)) {
    return;
  }
  if (isColumnSortSanitizeOutputEqual(current, next)) {
    return;
  }
  return patchBoth({ column_sort: next }, { column_sort: next });
}

/**
 * QB 字段结构变化后的列排序对齐。
 *
 * 触发场景举例：新增/删除维或指标、字段改名、改聚合方式、开关对比时间、切换对照组等。
 * 目标：列排序中的列集合与 QB 一致，维指同名字段的展示名、对比时间列、% 虚拟列等与当前 QB 匹配；
 * 用户在列排序里手工调整的相对顺序（在合法列范围内）尽量保留。
 *
 * 纯维/指区拖拽重排不由本 handler 处理，见 columnSortReorderHandler。
 */
export const columnSortSanitizeHandler: IQBLinkageHandler = {
  id: 'columnSort-sanitize',
  phase: 'sanitize',
  keys: ['column_sort'],
  subscribe: [
    'dimensionAdded',
    'dimensionRemoved',
    'metricAdded',
    'metricRemoved',
    'metricAggregateChanged',
    'fieldRenamed',
    'fieldIdentityRenamed',
    'compareTimeChanged',
    'userFilterGroupChanged',
  ],
  /**
   * QB 变更后对齐 column_sort（结构类事件：增删改名、对比时间、对照组等）。
   *
   * 计算最终列序：
   * 1. current ← draft.column_sort 优先，否则 base.column_sort（用户已保存顺序）
   * 2. canonical ← buildColumnSortFromQB(qbInput)（标准列序 / 参考答案，含维指同名展示名）
   * 3. next ← reconcileColumnSortWithQB(sortInput, qbInput, canonical)
   *    ①裁剪 ②裁光替换 ③补缺 ④⑤从 canonical 合并对比时间/指标展示名（维指同名、改聚合）
   * 3b. applyDisplayRename ← QB 展示名改名（fieldRenamed）；维度由 reconcile 跳过 merge，在此补齐；
   *     对比时间日期维（is_compare_date）固定跳过，仍由 applyCompareTimePatchIfNeeded 处理
   * 2b. migrateColumnSortIdentity ← 身份改名（fieldIdentityRenamed），在 reconcile 前执行以保留列序
   * 4. applyCompareTimePatchIfNeeded ← 从 columnFilters 解析对比时间并补齐展示名
   *    （compareTimeChanged 关闭时还原 is_compare_date；删维后插入独立对比时间列）
   *
   * metricAggregateChanged：仅改指标 aggregate、无增删时触发（见 detectMetricChanges）。
   * 若无实质变化（列 id 序、is_compare_date、展示名均一致）则不写 patch。
   * 指/维纯重排由 columnSortReorderHandler 处理。
   */
  apply(changeSet, ctx) {
    // hasPersistedColumnSort：base/draft 是否存在非空 column_sort。
    // 新图未拖过列序则 false，展示走 buildFromQB，本 handler 直接跳过。
    if (!hasPersistedColumnSort({ base: ctx.base, draft: ctx.draft })) {
      return;
    }

    // currentSort：取当前 persisted 列序，作为 reconcile 的「用户顺序」输入。
    const current = currentSort(ctx);

    // 列占比配置：draft 优先，供标准列序插入 % / dim_pct 虚拟列、裁剪时判断合法性。
    const pct = (ctx.draft.column_percentage ??
      ctx.base.column_percentage) as IColumnPercentageItem[] | undefined;
    const dimensionPct = (ctx.draft.dimension_percentage ??
      ctx.base.dimension_percentage) as IDimensionPercentageConfig | undefined;

    // qbInput：对齐所需的 QB 上下文（维/指/用户筛选/占比/对比时间），
    // 供 buildColumnSortFromQB 与 isColumnSortItemInQB 使用。
    const compareTime = resolveCompareTimeFromCtx(ctx);

    const qbInput = {
      columnDimensions: ctx.qb.columnDimensions,
      columnMetrics: ctx.qb.columnMetrics,
      columnFilters: ctx.qb.columnFilters,
      columnUserFilters: ctx.qb.columnUserFilters,
      hasUserFilters: ctx.qb.hasUserFilters,
      columnPercentage: Array.isArray(pct) ? pct : undefined,
      dimensionPercentage: dimensionPct,
      compareTime,
    };

    // buildColumnSortFromQB：按当前 QB 推导标准列序 canonical（对照组→维→指→%），
    // 不读用户顺序，仅作 reconcile 的参考答案。
    const canonical = buildColumnSortFromQB(qbInput);

    // QB 修改字段物理名（column_name）时，先迁移列排序里的列标识与展示名，
    // 避免 reconcile 仍按旧列名裁剪该行，导致用户已保存的列序丢失。
    // 自定义字段改名仍走 chartSettingsBridge；此处覆盖 pipeline 侧普通维/指的身份改名。
    let sortInput = current;
    const identityRenames = changeSet.renamed.filter(r => !r.displayOnly);
    if (identityRenames.length) {
      sortInput = migrateColumnSortIdentity(sortInput, identityRenames);
    }

    let next = reconcileColumnSortWithQB(sortInput, qbInput, canonical);

    // QB 仅改展示名（fieldRenamed）时：指标展示名 reconcile 已对齐，维度历史上不参与 merge，
    // 需在此把 QB 维区标签同步到 column_sort，否则表格列头与 QB 显示不一致。
    if (changeSet.renamed.some(r => r.displayOnly)) {
      next = applyDisplayRename(next, changeSet.renamed);
    }

    next = applyCompareTimePatchIfNeeded(next, ctx);

    return finalizeColumnSortPatch(ctx, current, next);
  },
};

/**
 * QB 维区/指区拖拽重排后的列排序对齐。
 *
 * 业务预期：用户在 QB 调整维或指标的先后顺序后，表格列顺序应跟随 QB 中对应字段的顺序
 * （维与维、指标与指标分别对齐；维指可交错摆放的既有规则不变）。
 * 若此时设置面板里还有未 Confirm 的列序草稿，只要 QB 确实改了顺序，仍应以 QB 为准写回。
 */
export const columnSortReorderHandler: IQBLinkageHandler = {
  id: 'columnSort-reorder',
  phase: 'sync',
  keys: ['column_sort'],
  subscribe: ['dimensionReordered', 'metricReordered'],
  /**
   * QB 指区或维区拖拽重排后，将 persisted column_sort 与当前 QB 列表顺序对齐。
   * 指区、维区分开处理（与 changeSet 事件一致），避免只改指区时误动维区相对位置。
   *
   * - metricReordered：realignMetricsToQBOrder（必要时收拢 CG 后、首个维前的指标到维区后，再指区排序）
   * - dimensionReordered：realignDimensionsToQBOrder（仅 QB 维区中的 dimension）
   * - 对比时间开启时：realign 后补齐 is_compare_date（不改 column_display_*）
   */
  apply(changeSet, ctx) {
    if (!hasPersistedColumnSort({ base: ctx.base, draft: ctx.draft })) {
      return;
    }

    const current = currentSort(ctx);
    const opsCtx = buildOpsCtx(ctx);
    let next = current;

    if (changeSet.events.has('dimensionReordered')) {
      next = realignDimensionsToQBOrder(next, opsCtx);
    }
    if (changeSet.events.has('metricReordered')) {
      next = realignMetricsToQBOrder(next, opsCtx);
    }

    next = applyCompareTimePatchIfNeeded(next, ctx);

    return finalizeColumnSortPatch(ctx, current, next);
  },
};
